<template>
  <sticky-header-layout>
    <template #header>
      <list-page-header>
        <div slot="right">
          <el-button
            :disabled="isSaving"
            size="mini"
            type="primary"
            icon="el-icon-back"
            @click="handleCancel"
          >
            {{ $t('button.back') }}
          </el-button>
          
          <el-button
            :disabled="isLoading"
            :loading="isSaving"
            size="mini"
            type="danger"
            icon="el-icon-upload2"
            @click="handleSave"
          >
            {{ $t('button.save') }}
          </el-button>
        </div>
      </list-page-header>
    </template>

    <div v-loading="isLoading">
      <el-form
        v-loading="isLoading"
        ref="form"
        :disabled="isSaving"
        :model="form"
        :rules="formRules"
        class="round-border-form"
        label-position="top"
        label-width="200"
      >
        <h4 class="title">Profile Detail</h4>

        <div class="form-group">
          <el-form-item
            label="Name"
            prop="name"
          >
            <el-input v-model="form.name" />
          </el-form-item>
        </div>

        <div class="form-group">
          <el-form-item
            label="Gender"
            prop="gender"
          >
            <el-select
              v-model="form.gender"
              :placeholder="$t('placeholder.select')"
            >
              <el-option
                label="Male"
                value="M"
              />
              <el-option
                label="Female"
                value="F"
              />
            </el-select>
            <!-- <el-input v-model="form.gender" /> -->
          </el-form-item>
        </div>
        
        <div class="form-group">
          <el-form-item
            label="Email"
            prop="email"
          >
            <el-input v-model="form.email" />
          </el-form-item>
        </div>

        <div class="form-group">
          <el-form-item
            label="Birthday"
            prop="birthday"
          >
            <el-date-picker
              v-model="form.birthday"
              :default-value="new Date()"
              type="date"
              value-format="yyyy-MM-dd"/>
            <!-- <el-input v-model="form.birthday" /> -->
          </el-form-item>
        </div>

        <div class="form-group">
          <el-form-item
            label="Size (Top)"
            prop="top_size"
          >
            <el-input v-model="form.top_size" />
          </el-form-item>
        </div>
        
        <div class="form-group">
          <el-form-item
            label="Size (Bottom)"
            prop="bottom_size"
          >
            <el-input v-model="form.bottom_size" />
          </el-form-item>
        </div>

        <div class="form-group">
          <el-form-item
            label="Size (Shoes)"
            prop="shoes_size"
          >
            <el-input v-model="form.shoes_size" />
          </el-form-item>
        </div>
        
        <div class="form-group">
          <el-form-item
            label="Favourite Brands"
            prop="favourite_brands"
          >
            <el-input v-model="form.favourite_brands" />
          </el-form-item>
        </div>

        <div class="form-group">
          <el-form-item
            label="Average Monthly Spend"
            prop="average_monthly_spend"
          >
            <el-input v-model="form.average_monthly_spend" />
          </el-form-item>
        </div>

        <div class="form-group">
          <el-form-item
            label="Remarks"
            prop="remarks"
          >
            <el-input v-model="form.remarks" />
          </el-form-item>
        </div>
      </el-form>
    </div>
  </sticky-header-layout>
</template>

<script>
import { createComponent, computed, reactive, ref, watch, onBeforeMount } from '@vue/composition-api'
import ListPageHeader from '@/views/layout/components/ListPageHeader.vue'

import PrivateClientsService from '../../api/private-clients'
import { i18n } from '@/lang'

const PrivateClientProfileDetail = createComponent({
  name: 'PrivateClientProfileDetail',
  components: {
    ListPageHeader,
  },
  props: {
    id: {
      type: [String, Number],
      default: undefined,
    },
  },
  setup({ id }, { root, refs }) {
    const { $router, $route } = root

    const isLoading = ref(false)
    const isSaving = ref(false)

    const form = reactive({
      private_client_id: $route.params.privateClientId,
      name: null,
      gender: null,
      email: null,
      birthday: null,
      top_size: null,
      bottom_size: null,
      shoes_size: null,
      favourite_brands: null,
      average_monthly_spend: 0,
      remarks: null,
    })

    const formRules = reactive({
      name: [{ required: true, message: i18n.t('error.thisFieldIsRequired'), trigger: 'blur' }],
    })

    const goToClientDetailPage = () => {
      $router.push({ path: `/private-client/client/edit/${$route.params.privateClientId}` })
    }

    const handleSaveSuccess = () => {
      isSaving.value = false

      goToClientDetailPage()
    }

    const handleSaveFail = (error) => {
      isSaving.value = false

      root.$alert(`Error: ${error}`, root.$t('error.somethingWentWrong'), {
        type: 'error',
        confirmButtonText: root.$t('button.ok'),
      })
    }

    onBeforeMount(async () => {
      if (id) {
        getDetail(id)
      }
    })

    const getDetail = async (id) => {
      isLoading.value = true

      try {
        const { data } = await PrivateClientsService.getProfile(id)
        
        Object.keys(data).forEach((key) => {
          if (key === 'type' && !data[key]) return
          form[key] = data[key]
        })
      } catch (error) {
        root.$alert(root.$t('error.recordNotFound'), root.$t('button.error'), {
          confirmButtonText: root.$t('button.ok'),
          type: 'error',
        }).then(() => {
          goToClientDetailPage()
        })
      } finally {
        isLoading.value = false
      }
    }

    const save = async (form) => {
      isSaving.value = true

      if (id) {
        try {
          await PrivateClientsService.editProfile(id, form)
          handleSaveSuccess()
        } catch (error) {
          handleSaveFail(error)
        }
      } else {
        try {
          await PrivateClientsService.createProfile(form)
          handleSaveSuccess()
        } catch (error) {
          handleSaveFail(error)
        }
      }
    }

    const handleCancel = () => goToClientDetailPage()

    const handleSave = () => {
      refs.form.validate().then(() => {
        save(form)
      }).catch((error) => {
        root.$alert(root.$t('error.pleaseFillInAndCheckErrors'), root.$t('common.oops'), {
          confirmButtonText: root.$t('button.ok'),
        })
      })
    }

    return {
      form,
      formRules,
      handleCancel,
      handleSave,
      isLoading,
      isSaving,
    }
  },
})

export default PrivateClientProfileDetail
</script>

<style lang="scss" scoped>
@import "~@/assets/styles/view-base.scss";
@import "~@/assets/styles/form.scss";
</style>